<script setup>
import { ref, computed, watch } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useMessagingStore } from '@/stores/messaging.store'

const props = defineProps({
  modelValue: Boolean,
})

const emit = defineEmits(['update:modelValue', 'conversation-created'])

const messagingStore = useMessagingStore()

const memberSearch = ref('')
const allFriends = ref([])
const loadingMembers = ref(false)
const selectedMembers = ref([])
const submitting = ref(false)
const errorMsg = ref('')

const memberSearchResults = computed(() => {
  const q = memberSearch.value.trim().toLowerCase()
  if (q.length < 1) return allFriends.value

  return allFriends.value.filter(u => {
    const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase()
    const company = (u.company || '').toLowerCase()
    
    return name.includes(q) || company.includes(q)
  })
})

watch(
  () => props.modelValue,
  val => {
    if (val) loadFriends()
  }
)

async function loadFriends() {
  loadingMembers.value = true
  try {
    const axios = (await import('@axios')).default
    const { data } = await axios.get('/user/', { params: { following: true } })
    const friends = Array.isArray(data) ? data : (data.users || [])
    allFriends.value = friends.sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase()
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase()
      
      return nameA.localeCompare(nameB)
    })
  } catch {
    allFriends.value = []
  } finally {
    loadingMembers.value = false
  }
}

async function addMember(member) {
  if (selectedMembers.value.some(m => m._id === member._id)) return
  selectedMembers.value.push(member)
  memberSearch.value = ''
  await openMemberThread()
}

function removeMember(memberId) {
  selectedMembers.value = selectedMembers.value.filter(m => m._id !== memberId)
}

async function openMemberThread() {
  if (selectedMembers.value.length === 0) return
  submitting.value = true
  errorMsg.value = ''
  try {
    const conv = await messagingStore.createConversation({
      type: 'member-member',
      memberIds: selectedMembers.value.map(m => m._id),
    })
    emit('conversation-created', conv._id)
    handleClose()
  } catch (err) {
    errorMsg.value = err.response?.data?.message || err.message || 'Failed to open conversation.'
  } finally {
    submitting.value = false
  }
}

function handleClose() {
  emit('update:modelValue', false)
  memberSearch.value = ''
  selectedMembers.value = []
  allFriends.value = []
  errorMsg.value = ''
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="500"
    @update:model-value="handleClose"
  >
    <VCard>
      <VCardTitle class="pa-4 pb-0">
        New Message
      </VCardTitle>

      <VCardText class="pa-4">
        <div class="mb-3">
          <!-- Selected friend -->
          <div
            v-if="selectedMembers.length"
            class="d-flex align-center gap-3 pa-2 rounded border mb-2"
          >
            <VAvatar
              size="36"
              color="grey-lighten-3"
            >
              <VImg
                v-if="selectedMembers[0].profileImageId"
                :src="mediaUrl(selectedMembers[0].profileImageId, 'thumb')"
              />
              <span
                v-else
                class="text-caption"
              >{{ selectedMembers[0].firstName?.[0] }}{{ selectedMembers[0].lastName?.[0] }}</span>
            </VAvatar>
            <div class="flex-1">
              <div class="text-body-2 font-weight-medium">
                {{ selectedMembers[0].firstName }} {{ selectedMembers[0].lastName }}
              </div>
              <div
                v-if="selectedMembers[0].title || selectedMembers[0].company"
                class="text-caption text-medium-emphasis"
              >
                {{ [selectedMembers[0].title, selectedMembers[0].company].filter(Boolean).join(' at ') }}
              </div>
            </div>
            <VBtn
              icon
              size="x-small"
              variant="text"
              color="medium-emphasis"
              @click="removeMember(selectedMembers[0]._id)"
            >
              <VIcon size="16">
                mdi-close
              </VIcon>
            </VBtn>
          </div>

          <!-- Search (only shown when no friend selected) -->
          <template v-else>
            <VTextField
              v-model="memberSearch"
              placeholder="Search friends…"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="solo-filled"
              flat
              rounded
              hide-details
              :loading="loadingMembers"
              class="conv-search"
            />

            <VList
              v-if="memberSearchResults.length"
              class="search-results border rounded mt-1"
              density="compact"
            >
              <VListItem
                v-for="member in memberSearchResults"
                :key="member._id"
                :title="`${member.firstName} ${member.lastName}`"
                :subtitle="[member.title, member.company].filter(Boolean).join(' at ') || ''"
                class="cursor-pointer"
                @click="addMember(member)"
              >
                <template #prepend>
                  <VAvatar
                    size="32"
                    color="grey-lighten-3"
                    class="mr-2"
                  >
                    <VImg
                      v-if="member.profileImageId"
                      :src="mediaUrl(member.profileImageId, 'thumb')"
                    />
                    <span
                      v-else
                      class="text-caption"
                    >{{ member.firstName?.[0] }}{{ member.lastName?.[0] }}</span>
                  </VAvatar>
                </template>
              </VListItem>
            </VList>

            <div
              v-else-if="!loadingMembers && allFriends.length === 0"
              class="text-caption text-medium-emphasis mt-2"
            >
              No mutual friends found.
            </div>
          </template>
        </div>

        <div
          v-if="submitting"
          class="d-flex justify-center py-4"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="28"
          />
        </div>

        <VAlert
          v-if="errorMsg"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ errorMsg }}
        </VAlert>
      </VCardText>

      <VCardActions class="pa-4 pt-0">
        <VSpacer />
        <VBtn
          variant="text"
          @click="handleClose"
        >
          Cancel
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.search-results {
  max-height: 200px;
  overflow-y: auto;
}

.conv-search :deep(.v-field) {
  font-size: 16px;
  background: var(--ds-surface-alt, #f8f8f9);
}
</style>
